import { useState, useRef, useEffect } from 'react';
import { searchProducts } from '../lib/api';
import { Search, Package, MapPin, Store, BookmarkCheck, Warehouse, Clock, Camera } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';

export const SearchPage = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const inputRef = useRef(null);

    // Auto-select text when query changes (for barcode scanners)
    useEffect(() => {
        if (query && inputRef.current) {
            // Small delay to ensure the value is set
            setTimeout(() => {
                inputRef.current?.select();
            }, 50);
        }
    }, [searched]); // Select after search completes

    const handleSearch = async (e) => {
        e?.preventDefault();
        if (!query.trim()) {
            toast.error('Introdu un termen de căutare');
            return;
        }
        
        try {
            setLoading(true);
            setSearched(true);
            const response = await searchProducts(query);
            setResults(response.data);
            // Select text after search so next scan replaces it
            setTimeout(() => inputRef.current?.select(), 100);
        } catch (err) {
            toast.error('Eroare la căutare');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle barcode scan from camera
    const handleBarcodeScan = (barcode) => {
        setQuery(barcode);
        setShowScanner(false);
        // Auto-search after scan
        setTimeout(async () => {
            try {
                setLoading(true);
                setSearched(true);
                const response = await searchProducts(barcode);
                setResults(response.data);
            } catch (err) {
                toast.error('Eroare la căutare');
            } finally {
                setLoading(false);
            }
        }, 100);
    };

    return (
        <div data-testid="search-page">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Căutare Produs
                </h1>
                <p className="text-zinc-500 mt-1">Găsește produse și vezi unde sunt stocate</p>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="mb-8">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="Caută după nume sau cod de bare..."
                            data-testid="global-search-input"
                            className="pl-12 h-14 text-base sm:text-lg bg-white"
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            onClick={() => setShowScanner(true)}
                            data-testid="search-camera-button"
                            className="h-14 px-4 bg-rose-500 hover:bg-rose-600"
                        >
                            <Camera size={22} />
                        </Button>
                        <Button
                            type="submit"
                            data-testid="global-search-button"
                            className="h-14 flex-1 sm:flex-none sm:w-auto px-6 sm:px-8 bg-zinc-900 hover:bg-zinc-800"
                            disabled={loading}
                        >
                            <Search size={20} className="mr-2" />
                            Caută
                        </Button>
                    </div>
                </div>
            </form>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleBarcodeScan}
            />

            {/* Results */}
            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-6">
                            <Skeleton className="h-6 w-1/3 mb-4" />
                            <div className="grid grid-cols-3 gap-4">
                                <Skeleton className="h-20" />
                                <Skeleton className="h-20" />
                                <Skeleton className="h-20" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : searched && results.length === 0 ? (
                <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
                    <Package size={64} className="mx-auto text-zinc-300 mb-4" />
                    <h3 className="text-lg font-medium text-zinc-900">Niciun rezultat</h3>
                    <p className="text-zinc-500 mt-1">Nu am găsit produse pentru "{query}"</p>
                </div>
            ) : results.length > 0 ? (
                <div className="space-y-4">
                    {results.map((product) => (
                        <div
                            key={product.id}
                            data-testid={`search-result-${product.id}`}
                            className="bg-white border border-zinc-200 rounded-lg p-6"
                        >
                            {/* Product Header */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {product.name}
                                    </h2>
                                    {product.barcode && (
                                        <p className="text-sm font-mono text-zinc-500 mt-1">
                                            Cod: #{product.barcode}
                                        </p>
                                    )}
                                    {product.category && (
                                        <span className="inline-block text-xs bg-zinc-100 text-zinc-600 px-2 py-1 rounded mt-2">
                                            {product.category}
                                        </span>
                                    )}
                                </div>
                                {product.price > 0 && (
                                    <div className="text-right">
                                        <span className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                            {product.price.toFixed(2)}
                                        </span>
                                        <span className="text-zinc-500 ml-1">RON</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Location Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Depozit */}
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Warehouse size={18} className="text-amber-600" />
                                        <span className="font-medium text-amber-700">Depozit</span>
                                    </div>
                                    <p className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {product.depozit_quantity || 0} buc.
                                    </p>
                                    {product.depozit_locations && product.depozit_locations.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {product.depozit_locations.map((loc, i) => (
                                                <span key={i} className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                                                    <MapPin size={12} />
                                                    {loc}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Store */}
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Store size={18} className="text-emerald-600" />
                                        <span className="font-medium text-emerald-700">Magazin</span>
                                    </div>
                                    <p className="text-2xl font-bold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {product.store_quantity || 0} buc.
                                    </p>
                                    <p className="text-xs text-emerald-600 mt-1">Disponibil pentru vânzare</p>
                                </div>
                                
                                {/* Comenzi */}
                                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <BookmarkCheck size={18} className="text-violet-600" />
                                        <span className="font-medium text-violet-700">Comenzi</span>
                                    </div>
                                    <p className="text-2xl font-bold text-violet-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {product.reserved_quantity || 0} buc.
                                    </p>
                                    <p className="text-xs text-violet-600 mt-1">În comenzi active</p>
                                </div>
                                
                                {/* Rezervări */}
                                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Clock size={18} className="text-teal-600" />
                                        <span className="font-medium text-teal-700">Rezervări</span>
                                    </div>
                                    <p className="text-2xl font-bold text-teal-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {product.reservations_quantity || 0} buc.
                                    </p>
                                    <p className="text-xs text-teal-600 mt-1">Rezervate pentru clienți</p>
                                </div>
                            </div>
                            
                            {/* Total */}
                            <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between">
                                <span className="text-sm text-zinc-500">Total inventar:</span>
                                <span className="font-semibold text-zinc-900">
                                    {(product.depozit_quantity || 0) + (product.store_quantity || 0) + (product.reserved_quantity || 0) + (product.reservations_quantity || 0)} bucăți
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
                    <Search size={64} className="mx-auto text-zinc-300 mb-4" />
                    <h3 className="text-lg font-medium text-zinc-900">Caută un produs</h3>
                    <p className="text-zinc-500 mt-1">Introdu numele sau codul de bare pentru a vedea locația</p>
                </div>
            )}
        </div>
    );
};

export default SearchPage;
